import axios from './axios.interceptors';
import qs from 'querystring';

export interface RegisterPayload {
  fullName: string;
  email: string;
  phoneNumber: string;
  password: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
  gender: string;
  dateOfBirth: string;
}

export interface ChangePasswordPayload {
  oldPassword: string;
  newPassword: string;
}

export interface AccountData {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
  gender: string;
  status: boolean;
  dateOfBirth: string;
  reservations: any[];
}

export interface UpdateAccountPayload {
  fullName: string;
  phoneNumber: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;
  gender: string;
  dateOfBirth: string;
}

class AuthService {
  public login = async (payload: any): Promise<any> => {
    try {
      const response = await axios.post(
        '/oauth/token',
        qs.stringify({
          grant_type: 'password',
          username: payload.email,
          password: payload.password,
        }),
        {
          headers: {
            'Authorization': `Basic ${btoa('b33mt1cket_cl13nt_1d:B3mt16%37-23cr37')}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      const data = response.data;
      return {
        ...data,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      };
    } catch (err: any) {
      console.log("Login error:", err.response?.data || err);

      const backendMessage =
        err.response?.data?.error_description ||
        err.response?.error_description ||
        err.data?.error_description  ||
        err?.error_description  ||
        "Login failed";

      throw new Error(backendMessage);
    }
  }

  public register = async (payload: RegisterPayload): Promise<any> => {
    try {
      const response = await axios.post('/api/v1/customers', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public verifyEmail = async (token: string): Promise<any> => {
    try {
      const response = await axios.post(
        `/api/v1/customers/verify-email?token=${token}`,
        {},
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public changePassword = async (payload: ChangePasswordPayload): Promise<any> => {
    try {
      const response = await axios.post(
        '/api/v1/account/password',
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      return response.data;
    } catch (err: any) {
      console.log("Change password error:", err.response?.data || err);

      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to change password";

      throw new Error(backendMessage);
    }
  }

  public getAccount = async (): Promise<AccountData> => {
    try {
      const accessToken = localStorage.getItem("access_token");
  
      const response = await axios.get('/api/v1/account', {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}` 
        }
      });
      return response.data;
    } catch (err: any) {
      console.log("Get account error:", err.response?.data || err);

      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch account data";

      throw new Error(backendMessage);
    }
  }

  public updateAccount = async (payload: UpdateAccountPayload): Promise<any> => {
    try {
      const response = await axios.put('/api/v1/account', payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (err: any) {
      console.log("Update account error:", err.response?.data || err);

      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to update account";

      throw new Error(backendMessage);
    }
  }

  public rfshToken = async (payload: any): Promise<any> => {
    try {
      const refToken = localStorage.getItem("refresh_token")
      const response = await axios.post('/oauth/token', qs.stringify({
        grant_type: 'refresh_token',
        refresh_token: refToken
      }), {
        headers: {
          'Authorization': `Basic ${btoa('b33mt1cket_cl13nt_1d:B3mt16%37-23cr37')}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  refreshTokenService(payload: any) {
    return axios.post("/oauth/token", payload, {
      headers: {
        Authorization: `Basic ${btoa("b33mt1cket_cl13nt_1d:B3mt16%37-23cr37")}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
  }
  refresh_token() {
    const token = localStorage.getItem("refresh_token");
    const requestBody = qs.stringify({
      grant_type: "refresh_token",
      refresh_token: token,
    });

    return new Promise((resolve, reject) => {
      return this.refreshTokenService(requestBody)
      .then((res) => {
        localStorage.setItem("refresh_token", res.data.refresh_token);
        localStorage.setItem("access_token", res.data.access_token);
        resolve(res);
      })
      .catch((err) => {
        reject(err);
        console.log(err);
      });
    });
  }

  public forgotPassword = async (email: any): Promise<any> => {
    try {
      const response = await axios.post(`/api/v1/account/forgot-password`, email);
      return response.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public resetPassword = async (body: { newPassword: string; confirmNewPassword: string }, token: string): Promise<any> => {
    try {
      const response = await axios.put(
        `/api/v1/account/reset-password?key=${token}`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }

  public isUserLoggedIn = () => {
    const token = localStorage.getItem("refresh_token");
    return !!token;
  }
}

export default new AuthService();
